import { cn } from "@/lib/utils";
import { creditsNavChipLabel } from "@repo/shared/credits";
import { useTranslation } from "@repo/shared/i18n";
import {
  creditsTotalSelector,
  creditsVisibleSelector,
  dailyRemainingSelector,
  hasTopUpsSelector,
} from "@repo/shared/redux";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";

import { CreditIcon } from "./CreditIcon";

type Props = {
  /** Mobile: number only, no suffix. */
  compact?: boolean;
  className?: string;
};

/**
 * Balance entry point in the top nav. Chrome-less on purpose — coin + count,
 * no pill or box, pending the container vs no-container trial.
 */
export const CreditsNavChip = ({ compact = false, className }: Props) => {
  const creditsVisible = useSelector(creditsVisibleSelector);
  const total = useSelector(creditsTotalSelector);
  const dailyRemaining = useSelector(dailyRemainingSelector);
  const hasTopUps = useSelector(hasTopUpsSelector);
  const navigate = useNavigate();
  const { t } = useTranslation("subscription");

  // See creditsVisibleSelector for the four cases where a number would be a
  // lie. The unlimited check there also narrows `total` to a number here.
  if (!creditsVisible || typeof total !== "number") return null;

  const { count, suffix } = creditsNavChipLabel({
    total,
    dailyRemaining,
    hasTopUps,
    compact,
  });

  /*
    The credits explainer is a section of the subscription page rather than a
    route of its own (DEV-3266), so this deep-links into it — `from` drives the
    scroll on arrival, the same way the club-import link already does.
  */
  return (
    <button
      type="button"
      onClick={() => navigate("/subscription", { state: { from: "credits" } })}
      aria-label={t("credits.chip.ariaLabel", {
        amount: count,
        suffix: suffix ?? t("credits.chip.left"),
      })}
      className={cn(
        "flex flex-row items-center gap-x-1.5 shrink-0 text-sm text-dark-secondary",
        // The hover is the same as the nav links, so it reads as a link.
        !compact &&
          "group rounded -mx-2 px-2 py-1.5 hover:bg-panel-foreground hover:text-white",
        className,
      )}
    >
      <CreditIcon />
      <span>
        <span className="font-bold">{count}</span>
        {/* data-squeeze: the desktop nav drops the suffix when the bar overflows. */}
        {suffix && (
          <span
            className="ml-1 font-normal text-dark-tertiary group-hover:text-white"
            data-squeeze="3"
          >
            {suffix}
          </span>
        )}
      </span>
    </button>
  );
};
